import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Globe, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Layout from '../components/layout/Layout';
import { institutionInfo, images } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.hero})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Have questions? We're here to help. Reach out to us through any of the channels below.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
                Get in Touch
              </h2>
              <div className="space-y-6">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#d4a855]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-1">Address</h4>
                    <p className="text-[#475569] text-sm">Baobab Institute of Tanzania (BIT)</p>
                    <p className="text-[#475569] text-sm">P.O. Box 35692,</p>
                    <p className="text-[#475569] text-sm">Mapinga, Bagamoyo District, Tanzania</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#d4a855]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-1">Email</h4>
                    <p className="text-[#475569] text-sm">{institutionInfo.email}</p>
                  </div>
                </div>

                {/* Website */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-[#d4a855]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-1">Website</h4>
                    <a 
                      href={`https://${institutionInfo.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2563eb] text-sm hover:underline block"
                    >
                      {institutionInfo.website}
                    </a>
                    <a 
                      href={`https://${institutionInfo.vocationalCenter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2563eb] text-sm hover:underline block"
                    >
                      {institutionInfo.vocationalCenter} <span className="text-[#d4a855]">(Vocational Center)</span>
                    </a>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#d4a855]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-1">Phone Numbers</h4>
                    {institutionInfo.phones.map((phone, idx) => (
                      <p key={idx} className="text-[#475569] text-sm">{phone}</p>
                    ))}
                  </div>
                </div>

                {/* Office Hours */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#d4a855]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-1">Office Hours</h4>
                    <p className="text-[#475569] text-sm">Mon - Fri: 8:00 AM - 5:00 PM</p>
                    <p className="text-[#475569] text-sm">Sat: 9:00 AM - 1:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8">
                <h3 className="font-semibold text-[#1e3a5f] mb-4">Our Location</h3>
                <div className="w-full h-[250px] bg-[#e2e8f0] rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.123456789!2d38.9!3d-6.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMjcnMDAuMCJTIDM4wrA1NCcwMC4wIkU!5e0!3m2!1sen!2stz!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="BIT Location"
                  />
                </div>
                <p className="text-[#475569] text-xs mt-2">
                  Mapinga, Bagamoyo District, Coast Region, Tanzania
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-[#1e3a5f]">
                    Send Us a Message
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isSubmitted ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-[#22c55e]" />
                      </div>
                      <h3 className="font-display text-xl font-semibold text-[#1e3a5f] mb-2">
                        Message Sent Successfully!
                      </h3>
                      <p className="text-[#475569] mb-6">
                        Thank you for contacting us. We will get back to you within 24-48 hours.
                      </p>
                      <Button
                        onClick={() => setIsSubmitted(false)}
                        variant="outline"
                        className="border-[#1e3a5f] text-[#1e3a5f]"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                            className="border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                            required
                            className="border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+255 XXX XXX XXX"
                            className="border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="What is this about?"
                            required
                            className="border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help you..."
                          rows={6}
                          required
                          className="border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full md:w-auto bg-[#1e3a5f] hover:bg-[#2563eb] text-white px-8"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Departments Contact */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            Department Contacts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Admissions Office', email: 'admissions@bit.ac.tz', phone: '+255 759 549 641' },
              { name: 'Academic Affairs', email: 'academics@bit.ac.tz', phone: '+255 750 559 641' },
              { name: 'Student Services', email: 'students@bit.ac.tz', phone: '+255 766 559 641' },
              { name: 'Finance Office', email: 'finance@bit.ac.tz', phone: '+255 696 929 641' },
            ].map((dept, index) => (
              <Card key={index} className="border-none shadow-sm">
                <CardContent className="pt-6 text-center">
                  <h4 className="font-semibold text-[#1e3a5f] mb-2">{dept.name}</h4>
                  <p className="text-[#475569] text-sm mb-1">{dept.email}</p>
                  <p className="text-[#475569] text-sm">{dept.phone}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
